import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh, Reply, Send, Visibility, VisibilityOff } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import useClientPagination from "../../common/use-client-pagination";
import AppChip from "../../common/app-chip";
import {
  INSTAGRAM_ACCOUNTS,
  INSTAGRAM_ACCOUNT_MEDIA,
  INSTAGRAM_COMMENTS,
  INSTAGRAM_COMMENTS_HIDE,
  INSTAGRAM_COMMENT_REPLY,
} from "../../config/urls";
import { errorMessage, formatDate, useInstagramApi, useInstagramSocket } from "./shared";

// Phase 3: comment-to-DM. Comments view (spec 10.5) to handle comments no rule caught. The comment
// rules themselves live with the other automations in Instagram Flow.

const STATUS = {
  unmatched: { label: "No rule", color: "default" },
  queued: { label: "DM queued", color: "info" },
  dm_sent: { label: "DM sent", color: "success" },
  failed: { label: "Failed", color: "error" },
  skipped: { label: "Skipped", color: "warning" },
};

const PRIVATE_LIMIT = 1000;

const InstagramComments = () => {
  const { request, notify, toast } = useInstagramApi();
  const [accounts, setAccounts] = useState([]);
  const [media, setMedia] = useState([]);
  const [filters, setFilters] = useState({ account: "", media: "", status: "", q: "" });
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ comments: [], queued_private_replies: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reply, setReply] = useState(null); // { comment, private, text }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    request("get", INSTAGRAM_ACCOUNTS)
      .then((res) => setAccounts(res.accounts.filter((a) => a.status !== "DISCONNECTED")))
      .catch(() => setAccounts([]));
  }, [request]);

  useEffect(() => {
    if (!filters.account) return setMedia([]);
    request("get", INSTAGRAM_ACCOUNT_MEDIA(filters.account))
      .then((res) => setMedia(res.media || []))
      .catch(() => setMedia([]));
  }, [filters.account, request]);

  useEffect(() => {
    const timer = setTimeout(() => setFilters((f) => (f.q === search ? f : { ...f, q: search })), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadComments = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
        setData(await request("get", `${INSTAGRAM_COMMENTS}?${params}`));
      } catch (error) {
        if (!quiet) notify(errorMessage(error, "Could not load comments."), "error");
      } finally {
        setLoading(false);
      }
    },
    [filters, request, notify],
  );

  useEffect(() => {
    loadComments();
    setSelected([]);
  }, [loadComments]);

  // Pushed by the server when a comment arrives or changes (incl. queued DMs going out in the background).
  useInstagramSocket(({ comments, reconnected }) => {
    if (comments || reconnected) loadComments(true);
  });

  const hide = async (ids, hidden) => {
    try {
      const res = await request("post", INSTAGRAM_COMMENTS_HIDE, { ids, hidden });
      const verb = hidden ? "hidden" : "unhidden";
      if (res.failed.length) {
        notify(`${res.updated.length} ${verb}, ${res.failed.length} failed: ${res.failed[0].error}`, "warning");
      } else {
        notify(`${res.updated.length} comment${res.updated.length === 1 ? "" : "s"} ${verb}.`);
      }
      setSelected([]);
      loadComments(true);
    } catch (error) {
      notify(errorMessage(error, "Could not update the comments."), "error");
    }
  };

  const sendReply = async () => {
    setBusy(true);
    try {
      await request("post", INSTAGRAM_COMMENT_REPLY(reply.comment.id), { text: reply.text, private: reply.private });
      notify(reply.private ? "Private reply queued. The chat is now in Conversation › Instagram." : "Reply posted under the comment.");
      setReply(null);
      loadComments(true);
    } catch (error) {
      notify(errorMessage(error, "Could not send the reply."), "error");
    } finally {
      setBusy(false);
    }
  };

  const setFilter = (field) => (event) =>
    setFilters({ ...filters, [field]: event.target.value, ...(field === "account" ? { media: "" } : {}) });

  const { pageRows: rows, pagination } = useClientPagination(data.comments, filters);
  // Select all = the rows on this page.
  const allSelected = rows.length > 0 && rows.every((c) => selected.includes(c.id));

  const columns = [
    {
      id: "post",
      label: "Post",
      render: (c) =>
        c.post ? (
          <Link href={c.post.permalink} target="_blank" rel="noopener noreferrer" title={c.post.caption}>
            <Avatar variant="rounded" src={c.post.thumbnail_url} sx={{ width: 40, height: 40 }} />
          </Link>
        ) : (
          <Avatar variant="rounded" sx={{ width: 40, height: 40 }}>?</Avatar>
        ),
    },
    {
      id: "from_username",
      label: "From",
      render: (c) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>@{c.from_username || "unknown"}</Typography>
          {c.is_reply && <Typography variant="caption" color="text.secondary">reply to a comment</Typography>}
        </Box>
      ),
    },
    {
      id: "text",
      label: "Comment",
      render: (c) => (
        <Typography variant="body2" sx={{ whiteSpace: "normal", maxWidth: 320, opacity: c.is_hidden ? 0.55 : 1 }}>
          {c.text}
          {c.is_hidden && <AppChip label="Hidden" sx={{ ml: 1 }} />}
        </Typography>
      ),
    },
    { id: "rule", label: "Rule", render: (c) => c.rule || "—" },
    {
      id: "status",
      label: "Status",
      render: (c) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          <Tooltip title={c.error || ""}>
            <span><AppChip {...(STATUS[c.status] || { label: c.status })} /></span>
          </Tooltip>
          {c.public_reply_sent && <AppChip label="Public reply" variant="outlined" color="success" />}
        </Box>
      ),
    },
    { id: "created_at", label: "When", render: (c) => formatDate(c.created_at) },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (c) => (
        <Box display="flex" justifyContent="flex-end">
          <Tooltip title="Reply publicly">
            <IconButton size="small" onClick={() => setReply({ comment: c, private: false, text: "" })}>
              <Reply fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={c.can_private_reply ? "Send private reply (DM)" : "Private reply already used, queued, or older than 7 days"}>
            <span>
              <IconButton size="small" disabled={!c.can_private_reply}
                onClick={() => setReply({ comment: c, private: true, text: "" })}>
                <Send fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={c.is_hidden ? "Unhide" : "Hide"}>
            <IconButton size="small" onClick={() => hide([c.id], !c.is_hidden)}>
              {c.is_hidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const renderComments = () => (
    <>
      {accounts.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Instagram account is connected. Connect one in Channel Onboarding.
        </Alert>
      )}
      {data.queued_private_replies > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.queued_private_replies} private {data.queued_private_replies === 1 ? "reply" : "replies"} queued.
          Instagram allows 750 private replies per hour per account; the rest go out automatically as the limit resets.
        </Alert>
      )}
      <Box display="flex" alignItems="center" mb={2} gap={1.5} flexWrap="wrap">
        <TextField select size="small" label="Account" value={filters.account} sx={{ minWidth: 170 }}
          onChange={setFilter("account")}>
          <MenuItem value="">All accounts</MenuItem>
          {accounts.map((a) => <MenuItem key={a.id} value={a.id}>@{a.username}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Post" value={filters.media} sx={{ minWidth: 200 }}
          onChange={setFilter("media")} disabled={!filters.account}
          helperText={filters.account ? undefined : "Pick an account first"}>
          <MenuItem value="">All posts</MenuItem>
          {media.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{m.caption || m.permalink}</Typography>
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Status" value={filters.status} sx={{ minWidth: 150 }}
          onChange={setFilter("status")}>
          <MenuItem value="">Any status</MenuItem>
          {Object.entries(STATUS).map(([value, s]) => <MenuItem key={value} value={value}>{s.label}</MenuItem>)}
          <MenuItem value="hidden">Hidden</MenuItem>
        </TextField>
        <TextField size="small" label="Search" placeholder="Text or username" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
        <Box flex={1} />
        <ButtonOne variant="outlined" startIcon={<Refresh />} title="Refresh" onClick={() => loadComments()} />
      </Box>
      {selected.length > 0 && (
        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
          <Typography variant="body2">{selected.length} selected</Typography>
          <ButtonOne variant="outlined" title="Hide" onClick={() => hide(selected, true)} />
          <ButtonOne variant="outlined" title="Unhide" onClick={() => hide(selected, false)} />
        </Box>
      )}
      <AppTable
        columns={columns}
        rows={rows}
        {...pagination}
        loading={loading}
        emptyMessage="No comments yet"
        selectable
        hideSelectAllWhenEmpty
        hasSelectedAll={allSelected}
        onSelectAll={() => setSelected(allSelected ? [] : rows.map((c) => c.id))}
        isRowSelected={(c) => selected.includes(c.id)}
        onSelectRow={(_, c) =>
          setSelected((ids) => (ids.includes(c.id) ? ids.filter((id) => id !== c.id) : [...ids, c.id]))
        }
      />
    </>
  );

  return (
    <>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Comments on your posts and what the bot did with them. Reply, send a private DM or hide the ones no rule
        caught. Comment-to-DM rules are set up in the Automations tab.
      </Typography>

      {renderComments()}

      <Dialog open={Boolean(reply)} onClose={() => setReply(null)} fullWidth maxWidth="sm">
        {reply && (
          <>
            <DialogTitle>
              {reply.private ? "Send a private reply" : "Reply publicly"} to @{reply.comment.from_username}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" mb={2}>“{reply.comment.text}”</Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label={reply.private ? "Private DM" : "Public reply"}
                InputLabelProps={{ shrink: true }}
                value={reply.text}
                inputProps={reply.private ? { maxLength: PRIVATE_LIMIT } : undefined}
                onChange={(e) => setReply({ ...reply, text: e.target.value })}
                helperText={
                  reply.private
                    ? `Meta allows one private reply per comment, within 7 days. It opens a chat in Conversation › Instagram and pauses the bot there. ${reply.text.length}/${PRIVATE_LIMIT}`
                    : "Everyone can see this under the post."
                }
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <ButtonOne variant="outlined" title="Cancel" onClick={() => setReply(null)} />
              <ButtonOne title={busy ? "Sending…" : "Send"} onClick={sendReply} disabled={busy || !reply.text.trim()} />
            </DialogActions>
          </>
        )}
      </Dialog>
      {toast}
    </>
  );
};

export default InstagramComments;
